import { Button, Chip, InputAdornment, Stack, Typography } from "@mui/material";
import { CustomInput } from "global/components";
import CustomDialog from "global/components/CustomDialog/CustomDialog";
import strings from "global/constants/StringConstants";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import _, { cloneDeep, filter } from "lodash";
import { useContext, useState } from "react";
import { sendTestEmails } from "screens/Campaigns/CampaignService";
import { CampaignContext } from "../Context";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { useAppSelector } from "utils/hooks";
import { isTrial, selectEmail } from "redux/authSlice";

interface CustomProps {
  handleDialogClose: Function;
  isDialogOpen: boolean;
}

const TestModal = (props: CustomProps) => {
  const { submitCampaign } = useContext(CampaignContext);
  const isTrialAccount = useAppSelector(isTrial);
  const loggedInUserEmail = useAppSelector(selectEmail);
  const [testEmailIds, setTestEmailIds] = useState<string[]>([
    loggedInUserEmail,
  ]);
  const [testEmail, setTestEmail] = useState({
    value: "",
    error: "",
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateEmailAddress = () => {
    let isValid = true;
    const clonedAddTestEmailValue = cloneDeep(testEmail);
    const checkExistingValue = testEmailIds?.some(
      (item: any) => item === (testEmail?.value).toLowerCase().trim()
    );
    if (checkExistingValue) {
      clonedAddTestEmailValue.error = "You cannot add duplicate emails.";
      isValid = false;
    }
    if (!strings.regex.test(testEmail.value)) {
      clonedAddTestEmailValue.error = "Please enter valid email";
      isValid = false;
    }
    if (testEmailIds?.length > strings.TEST_MAIL_LIMIT) {
      clonedAddTestEmailValue.error = `Test emails can't be sent to more than ${
        strings.TEST_MAIL_LIMIT + 1
      } emails`;
      isValid = false;
    }
    setTestEmail(clonedAddTestEmailValue);
    return isValid;
  };

  const addTestEmail = (event: any) => {
    if (validateEmailAddress()) {
      if (event.key === "Enter") {
        event.preventDefault();
        const clonedTestEmailIds = cloneDeep(testEmailIds);
        clonedTestEmailIds.push(testEmail.value.toLowerCase().trim());
        setTestEmailIds(clonedTestEmailIds);
        setTestEmail({ value: "", error: "" });
      }
    }
  };

  const sendTestEmail = async () => {
    try {
      setIsLoading(true);
      const campaign = cloneDeep(submitCampaign);
      campaign.campaign.id = 0;
      campaign.campaign.testIds = testEmailIds;
      await sendTestEmails(campaign);
      setIsLoading(false);
      openSuccessNotification("Test email(s) sent successfully");
      props.handleDialogClose();
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const hideORshowMultipleSendEmails = () => {
    if (isTrialAccount) {
      return false;
    }
    if (
      !isTrialAccount &&
      (strings.MAILZZY_DOMAINS.includes(
        submitCampaign.campaignData.domainName
      ) ||
        submitCampaign.campaignData.domainName === "")
    ) {
      return false;
    }
    if (
      !isTrialAccount &&
      !strings.MAILZZY_DOMAINS.includes(submitCampaign.campaignData.domainName)
    ) {
      return true;
    }
  };

  const getDescriptionMessage = () => {
    if (isTrialAccount) {
      return "During your free trial, test emails can only be sent to the logged-in email address for security reasons. No additional email addresses can be added.";
    }

    if (
      !isTrialAccount &&
      (strings.MAILZZY_DOMAINS.includes(
        submitCampaign.campaignData.domainName
      ) ||
        submitCampaign.campaignData.domainName === "")
    ) {
      return "Test emails can only be sent to the logged-in email address for security reasons. You will not be able to add additional email addresses. To send campaigns to other recipients, please use your custom domain.";
    }

    if (
      !isTrialAccount &&
      !strings.MAILZZY_DOMAINS.includes(submitCampaign.campaignData.domainName)
    ) {
      return "Enter upto 10 email addresses to test your email template.";
    }
  };

  const getBody = () => {
    return (
      <Stack my={1} spacing={1}>
        <Stack spacing={2} mb={1}>
          <Typography>{getDescriptionMessage()}</Typography>
          <CustomInput
            id="test-email"
            placeHolder="Enter email address and press enter"
            required
            type="text"
            name="tag"
            onChange={(event: any) =>
              setTestEmail({ value: event.target.value, error: "" })
            }
            onKeyPress={(event: any) => addTestEmail(event)}
            value={testEmail.value}
            propsToInputElement={{ maxLength: 100 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <span style={{ fontSize: "0.75rem" }}>
                    {testEmail.value.length} / 100
                  </span>
                </InputAdornment>
              ),
            }}
            disabled={!hideORshowMultipleSendEmails()}
            error={testEmail.error}
            helperText={testEmail.error}
          />
        </Stack>
        <Stack direction="row" flexWrap="wrap">
          {testEmailIds.map((email, index) => (
            <Chip
              key={index}
              label={email}
              onDelete={
                hideORshowMultipleSendEmails()
                  ? () => {
                      const clonedTestEmailIds = cloneDeep(testEmailIds);
                      clonedTestEmailIds.splice(index, 1);
                      setTestEmailIds(clonedTestEmailIds);
                    }
                  : undefined
              }
              sx={{ mr: 1, mt: 1 }}
            />
          ))}
        </Stack>
      </Stack>
    );
  };

  const getFooter = () => {
    return (
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => props.handleDialogClose()}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={sendTestEmail}
          disabled={!testEmailIds.length}
        >
          Send test email
        </Button>
      </Stack>
    );
  };

  return (
    <>
      <CustomDialog
        handleDialogClose={props.handleDialogClose}
        isDialogOpen={props.isDialogOpen}
        footer={getFooter()}
        body={getBody()}
        title={
          <Typography variant="h4" color="#7551D0">
            Send test email
          </Typography>
        }
        closeButtonVisibility
        closable
      />
      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default TestModal;
